import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Thermometer, Star, Users } from 'lucide-react';
import { CaseStudyHero } from '../components/CaseStudyHero';
import { CaseStudyContent } from '../components/CaseStudyContent';
import { CaseStudySection } from '../components/CaseStudySection';
import { MetricCard } from '../components/MetricCard';
import { QuoteBlock } from '../components/QuoteBlock';
import { ImageGallery } from '../components/ImageGallery';
import { Footer } from '../components/Footer';
import { FeatureModal } from '../components/FeatureModal';
import { motion } from 'framer-motion';
import { Zap, BarChart3 } from 'lucide-react';
import { CaseStudyTableOfContents } from '../components/CaseStudyTableOfContents';
const features = [{
  id: 1,
  title: 'Usage Guidance',
  description: 'Interactive onboarding that builds confidence and reduces returns.',
  image: 'https://cdn.magicpatterns.com/uploads/cZW5X1TYp3c6K6Pj4A45N1/embr-onboarding_copy.png',
  userInsight: 'We observed new Wave customers feeling confused and struggling to operate the Wave effectively for their hot flashes. Some questioned their purchase, and gave up quickly due to skepticism or a feeling of being overwhelmed and defeated.',
  solution: '10-step, interactive app onboarding, providing personalized guidance on how to use the Wave and the science behind how the technology works.',
  userOutcome: "Users can feel the Wave in action as they're learning how to operate it, gaining a better understanding of how the physical experience works, and more confidence that it will provide ongoing hot flash relief.",
  businessOutcome: 'Lower product return rates and increased overall device usage and app engagement.',
  quote: "Oh, that's why you call it the Wave - because the cooling comes in waves!"
}, {
  id: 3,
  title: 'Instant, Targeted Relief',
  description: 'Curated sessions for hot flashes, sleep, stress, and anxiety.',
  image: 'https://cdn.magicpatterns.com/uploads/2djSgf7HQTTHw87KWEFPEN/embr-targetedrelief.png',
  userInsight: "In the moment, many women struggled to start the Wave quickly enough, with just the right sensation, to relieve their hot flash. Additionally, most women weren't aware that the Wave could help relieve other symptoms of menopause.",
  solution: 'Curated sessions to address the most common types of hot flashes, as well as a variety of other session types to address menopausal symptoms like stress, anxiety, and trouble falling and staying asleep.',
  userOutcome: 'Users can quickly and easily find the exact sensation to relieve their particular discomforts, resulting in greater overall relief of menopausal symptoms.',
  businessOutcome: 'Increased customer usage due to additional symptom cluster solutions, resulting in overall greater lifetime value.',
  quote: "When I feel a hot flash coming on, I panic! I know I'm in for 10 minutes of absolute hell."
}, {
  id: 2,
  title: 'Thermal Customization',
  description: 'Personalized temperature and duration controls for optimal relief.',
  image: 'https://cdn.magicpatterns.com/uploads/iKAPpG1Z64Fetkiovxm1aZ/embr-cooling-customization.png',
  userInsight: "Many women found the default cooling sessions to be ineffective at reducing their hot flashes. Some complained that the device wasn't cold enough or that the cooling sensation didn't last as long as their hot flashes did.",
  solution: 'Custom controls for the duration and temperature of the Wave sensations. A simplified interface to adjust from both the app and device buttons with clear visual feedback of their adjustments.',
  userOutcome: 'Users have greater control over the sensation the device produces, and therefore a greater sense of relief from hot flashes.',
  businessOutcome: 'Increased customer satisfaction due to higher perceived efficacy of the device, and more data captured specific to user sensation preferences.',
  quote: 'Oooh, I can feel it working! So I push this button to make it even colder?'
}, {
  id: 4,
  title: 'Symptom Tracking',
  description: 'Pattern recognition to predict and mitigate hot flashes.',
  image: 'https://cdn.magicpatterns.com/uploads/65DhS72j13jrCRnpfLXUq4/embr-tracklearn.png',
  userInsight: 'Women had a vague sense that there were patterns in their hot flash occurrence, but were unaware that identifying patterns would be key to finding relief over time.',
  solution: 'Wave usage tracking to capture hot flash occurrence, severity, and duration.',
  userOutcome: 'Women have a clearer understanding of their patterns and can use that information to predict and mitigate hot flashes. Additionally, women found hot flash pattern data helpful in conversations with healthcare providers.',
  businessOutcome: 'Greater insight into customer behavior, leveraging data to inform the evolution of the product.',
  quote: "Gosh, I'm getting a hot flash right around noontime every day - that's just about when I start my student lectures."
}];
const sections = [{
  id: 'overview',
  title: 'Overview',
  description: 'Understanding the gap between technology and human need'
}, {
  id: 'challenge',
  title: 'The Challenge',
  description: "Why wasn't the first generation working?"
}, {
  id: 'team',
  title: 'The Team',
  description: 'Collaboration across the entire organization'
}, {
  id: 'research',
  title: 'Research & Discovery',
  description: 'Understanding the problem space'
}, {
  id: 'insights',
  title: 'Key Insights',
  description: 'The menopause journey revealed unexpected opportunities'
}, {
  id: 'ideation',
  title: 'Ideation & Planning',
  description: 'From big ideas to tough decision-making'
}, {
  id: 'solution',
  title: 'The Solution',
  description: 'A holistic ecosystem for relief'
}, {
  id: 'results',
  title: 'Results & Impact',
  description: 'Measurable outcomes that transformed the business'
}];
export function EmbrCaseStudy() {
  const [selectedFeature, setSelectedFeature] = useState<(typeof features)[0] | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openFeature = (feature: (typeof features)[0]) => {
    setSelectedFeature(feature);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };
  const goToPrevious = () => {
    if (!selectedFeature) return;
    const currentIndex = features.findIndex(f => f.id === selectedFeature.id);
    if (currentIndex > 0) {
      setSelectedFeature(features[currentIndex - 1]);
    }
  };
  const goToNext = () => {
    if (!selectedFeature) return;
    const currentIndex = features.findIndex(f => f.id === selectedFeature.id);
    if (currentIndex < features.length - 1) {
      setSelectedFeature(features[currentIndex + 1]);
    }
  };
  const currentIndex = selectedFeature ? features.findIndex(f => f.id === selectedFeature.id) : -1;
  return <div className="min-h-screen bg-white font-sans text-slate-900" data-id="element-241">
      <CaseStudyTableOfContents sections={sections} data-id="element-242" />

      {/* Back Button */}
      <Link to="/" className="fixed top-8 left-8 z-50 flex items-center gap-2 rounded-full bg-white/90 backdrop-blur-sm px-4 py-2 text-sm font-medium text-slate-700 shadow-lg transition-all hover:bg-white hover:shadow-xl hover:scale-105" data-id="element-243">
        <ArrowLeft size={16} data-id="element-244" />
        Back to Home
      </Link>

      {/* SECTION 1: HERO IMAGE */}
      <CaseStudyHero image="https://cdn.magicpatterns.com/uploads/p4Av6esYEFfWVBSwCnr4jm/embr-hero-relief.png" mobileImage="https://cdn.magicpatterns.com/uploads/pScQBCJhqZQh1G6drmHzgm/embr-mobilehero.png" data-id="element-245" />

      {/* SECTION 1.5: HERO CONTENT */}
      <CaseStudyContent title="Delivering hot flash relief" subtitle="Reinventing the Embr Wave for menopause" role="Lead Product Designer" timeline="2020 - 2022" client="Embr Labs" outcome="Launched brand new app" data-id="element-246" />

      {/* Stats Bridge Bar - overlaps hero and overview */}
      <div className="relative z-10 mx-auto max-w-4xl px-6 -mt-8 mb-[-3rem]" data-id="element-247">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        ease: 'easeOut'
      }} className="bg-white rounded-2xl shadow-xl border border-slate-100 px-6 py-8 md:px-10" data-id="element-248">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4" data-id="element-249">
            {[{
            value: '168%',
            label: 'Increase in efficacy',
            icon: Thermometer
          }, {
            value: '4.8/5',
            label: 'App Store rating',
            icon: Star
          }, {
            value: '200K',
            label: 'Active users',
            icon: Users
          }].map((stat, index) => <motion.div key={stat.label} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3 + index * 0.12,
            duration: 0.5
          }} className="flex items-center gap-4 md:justify-center" data-id="element-250">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-251">
                  <stat.icon className="w-5 h-5 text-blue-600" data-id="element-252" />
                </div>
                <div data-id="element-253">
                  <p className="text-2xl md:text-3xl font-bold text-blue-600 leading-none" data-id="element-254">
                    {stat.value}
                  </p>
                  <p className="text-sm text-slate-500 mt-1 leading-snug" data-id="element-255">
                    {stat.label}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </motion.div>
      </div>

      {/* SECTION 2: OVERVIEW/CONTEXT */}
      <CaseStudySection id="overview" title="Overview" subtitle="Understanding the gap between technology and human need." background="white" data-id="element-256">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2" data-id="element-257">
          <div className="space-y-6 text-lg text-slate-600" data-id="element-258">
            <p data-id="element-259">
              Embr Labs had launched the Wave 1, a wrist-worn wearable that
              provides thermal relief. However, the first generation device was
              facing significant challenges: high return rates and
              lower-than-expected customer satisfaction scores.
            </p>
            <p data-id="element-260">
              The company hypothesized that menopausal women were a key
              demographic, but the existing product wasn't meeting their
              specific needs. I led the research and design strategy to pivot
              the product specifically for menopause symptom relief, taking
              ownership of the entire discovery and design process.
            </p>
          </div>
          <div className="flex items-center justify-center rounded-2xl bg-blue-50 p-8" data-id="element-261">
            <div className="space-y-4" data-id="element-262">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-263">
                Key Objectives
              </h3>
              <ul className="space-y-3" data-id="element-264">
                {['Reduce return rates', 'Increase device efficacy', 'Design specifically for hot flashes', 'Create a companion app experience'].map((item, i) => <li key={i} className="flex items-start gap-3 text-slate-700" data-id="element-265">
                    <div className="mt-1.5 h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" data-id="element-266" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </CaseStudySection>

      {/* SECTION 3: CHALLENGE */}
      <CaseStudySection id="challenge" title="The Challenge" subtitle="Why wasn't the first generation working?" background="gray" data-id="element-267">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-268">
          <div className="space-y-6 text-lg text-slate-600" data-id="element-269">
            <p data-id="element-270">
              The Wave 1 was designed as a general-purpose thermal wellness
              device, but this broad positioning meant it wasn't solving any
              specific problem particularly well. Return rates were high, and
              user feedback indicated confusion about when and how to use the
              device.
            </p>
            <p data-id="element-271">
              I needed to answer critical questions: What would make this device
              indispensable for women experiencing menopause? What were the
              unmet needs that thermal technology could uniquely address? I set
              out to deeply understand both the physiological and emotional
              dimensions of this experience.
            </p>
          </div>

          <div className="relative" data-id="element-272">
            <img src="https://cdn.magicpatterns.com/uploads/3QSxydKNa8JzmnRX1gix8D/embr-v1.png" alt="Embr Wave 1 device and app interface" className="w-full h-auto rounded-xl shadow-xl" data-id="element-273" />
            <div className="mt-4 text-center" data-id="element-274">
              <span className="text-sm font-medium text-slate-500 uppercase tracking-wider" data-id="element-275">
                Wave 1: The Starting Point
              </span>
            </div>
          </div>
        </div>
      </CaseStudySection>

      {/* SECTION 3.5: THE TEAM */}
      <CaseStudySection id="team" title="The Team" subtitle="Collaboration across the entire organization." background="white" data-id="element-276">
        <div className="space-y-12" data-id="element-277">
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl" data-id="element-278">
            For two years, I worked hand-in-hand with almost every member of the
            35-person company, from customer support to mechanical engineering.
            I took a player-coach role, leading the design and research work
            streams while working hand-in-hand training-up Embr's green design
            talent.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-279">
            {/* Core Team */}
            <div data-id="element-280">
              <h3 className="text-xl font-bold text-blue-600 mb-4" data-id="element-281">
                The Core Team:
              </h3>
              <ul className="space-y-2" data-id="element-282">
                <li className="flex items-start gap-2 text-slate-700" data-id="element-283">
                  <span className="text-blue-500 mt-1" data-id="element-284">•</span>
                  <span data-id="element-285">UX Lead (myself)</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700" data-id="element-286">
                  <span className="text-blue-500 mt-1" data-id="element-287">•</span>
                  <span data-id="element-288">UX Designer</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700" data-id="element-289">
                  <span className="text-blue-500 mt-1" data-id="element-290">•</span>
                  <span data-id="element-291">Chief Product Officer</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700" data-id="element-292">
                  <span className="text-blue-500 mt-1" data-id="element-293">•</span>
                  <span data-id="element-294">Engineering Lead</span>
                </li>
              </ul>
            </div>

            {/* Stakeholders */}
            <div data-id="element-295">
              <h3 className="text-xl font-bold text-blue-600 mb-4" data-id="element-296">
                The Stakeholders:
              </h3>
              <ul className="space-y-2" data-id="element-297">
                <li className="flex items-start gap-2 text-slate-700" data-id="element-298">
                  <span className="text-blue-500 mt-1" data-id="element-299">•</span>
                  <span data-id="element-300">CEO + Founders</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700" data-id="element-301">
                  <span className="text-blue-500 mt-1" data-id="element-302">•</span>
                  <span data-id="element-303">Advisory Board</span>
                </li>
              </ul>
            </div>

            {/* Subject Matter Experts */}
            <div data-id="element-304">
              <h3 className="text-xl font-bold text-blue-600 mb-4" data-id="element-305">
                The Subject Matter Experts:
              </h3>
              <ul className="space-y-2" data-id="element-306">
                <li className="flex items-start gap-2 text-slate-700" data-id="element-307">
                  <span className="text-blue-500 mt-1" data-id="element-308">•</span>
                  <span data-id="element-309">Chief Medical Officer</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700" data-id="element-310">
                  <span className="text-blue-500 mt-1" data-id="element-311">•</span>
                  <span data-id="element-312">Customer Success</span>
                </li>
                <li className="flex items-start gap-2 text-slate-700" data-id="element-313">
                  <span className="text-blue-500 mt-1" data-id="element-314">•</span>
                  <span data-id="element-315">Device Engineers</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Iterative Approach Callout */}
          <div className="mt-16 pt-16 border-t border-slate-200" data-id="element-316">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-317">
              <div className="max-w-2xl" data-id="element-318">
                <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-319">
                  My Approach
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-320">
                  I combined Design Thinking with an Agile mindset, working
                  iteratively to test and refine solutions with real users
                  throughout the process.
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-2" data-id="element-321">
                {['Data-driven', 'Nimble', 'Inclusive', 'Experimentative', 'Iterative'].map(tag => <span key={tag} className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-full border border-blue-200" data-id="element-322">
                    {tag}
                  </span>)}
              </div>
            </div>
          </div>
        </div>
      </CaseStudySection>

      {/* SECTION 4: RESEARCH & DISCOVERY */}
      <CaseStudySection id="research" background="gray" data-id="element-323">
        <div className="space-y-8" data-id="element-324">
          <h2 className="text-3xl font-bold text-slate-900 md:text-4xl lg:text-5xl" data-id="element-325">
            Research & Discovery
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: '80px'
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3,
            duration: 0.8
          }} className="mt-4 h-1.5 bg-blue-500" data-id="element-326" />
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start" data-id="element-327">
            <div className="lg:col-span-3 space-y-6" data-id="element-328">
              <p className="text-xl text-slate-600 md:text-2xl leading-relaxed" data-id="element-329">
                Understanding the problem space through comprehensive user
                research and market analysis.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-330">
                {['Stakeholder interviews', 'Competitive analysis', 'Usability testing', 'Usage analytics', 'Heuristic evaluation'].map((activity, index) => <motion.span key={activity} initial={{
                opacity: 0,
                scale: 0.9
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.4 + index * 0.05,
                duration: 0.3
              }} className="px-2 py-1 text-xs font-medium text-slate-600 bg-slate-100 rounded-full border border-slate-200" data-id="element-331">
                    {activity}
                  </motion.span>)}
              </div>

              <p className="text-lg text-slate-700 leading-relaxed" data-id="element-332">
                To truly understand the problem, I knew we couldn't just rely on
                surveys. I conducted exploratory interviews with dozens of women
                in menopause to learn about their lived experiences. I then
                collaborated with Embr's Chief Medical Officer and a PhD student
                at UMass Amherst conducting a longitudinal study on the
                physiology of hot flashes to better understand the science.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed" data-id="element-333">
                Through this research, we uncovered a critical insight: hot
                flashes largely fell into three distinct types, each requiring a
                different thermal intervention. This discovery fundamentally
                shifted our approach from a "one-size-fits-all" cooling
                sensation to multiple, targeted cooling sessions.
              </p>
            </div>

            <div className="lg:col-span-2 overflow-hidden rounded-xl bg-slate-100 shadow-lg" data-id="element-334">
              <img src="https://cdn.magicpatterns.com/uploads/hbJqcph1ztnXT55JUhZbC1/embr-hotflashes.png" alt="Hot flash types and thermal intervention model" className="w-full h-auto object-cover" data-id="element-335" />
              <div className="bg-white p-4 text-sm text-slate-500 border-t border-slate-100 italic" data-id="element-336">
                Research revealed three distinct types of hot flashes, each
                requiring different thermal waveforms for optimal relief.
              </div>
            </div>
          </div>
        </div>
      </CaseStudySection>

      {/* SECTION 5: KEY INSIGHTS */}
      <CaseStudySection id="insights" background="white" data-id="element-337">
        <div className="space-y-8" data-id="element-338">
          <h2 className="text-3xl font-bold text-slate-900 md:text-4xl lg:text-5xl" data-id="element-339">
            Key Insights
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: '80px'
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3,
            duration: 0.8
          }} className="mt-4 h-1.5 bg-blue-500" data-id="element-340" />
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start" data-id="element-341">
            <div className="lg:col-span-3 space-y-6" data-id="element-342">
              <p className="text-xl text-slate-600 md:text-2xl leading-relaxed" data-id="element-343">
                The menopause journey revealed unexpected opportunities.
              </p>

              <div className="space-y-6 text-lg text-slate-600" data-id="element-344">
                <p data-id="element-345">
                  I mapped the end-to-end journey of menopause, identifying
                  three distinct stages: <strong data-id="element-346">Realization</strong>,{' '}
                  <strong data-id="element-347">Vulnerability</strong>, and{' '}
                  <strong data-id="element-348">Exasperation</strong>.
                </p>
                <p data-id="element-349">
                  Surprisingly, I discovered that hot flashes typically only
                  begin two-thirds of the way into this journey. This revealed a
                  massive opportunity that I brought to the team: the Wave could
                  address early-stage symptoms like stress, sleep disruption,
                  and "brain fog" long before hot flashes became the primary
                  concern.
                </p>

                <div className="border-l-4 border-blue-500 bg-blue-50/50 py-4 px-6 rounded-r" data-id="element-350">
                  <p className="text-slate-700 leading-relaxed" data-id="element-351">
                    "I feel like my body is betraying me. I just want to feel
                    like myself again."
                  </p>
                  <p className="text-sm text-slate-500 italic mt-2" data-id="element-352">
                    — Research Participant, Late Perimenopause
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 overflow-hidden rounded-xl bg-slate-100 shadow-lg" data-id="element-353">
              <img src="https://cdn.magicpatterns.com/uploads/gxW7BGX7EBXD2cu5DLbpgE/embr-menomodel.png" alt="Empowered Emily's Menopausal Journey" className="w-full h-auto object-cover" data-id="element-354" />
              <div className="bg-white p-4 text-sm text-slate-500 border-t border-slate-100 italic" data-id="element-355">
                Mapping the menopause journey with corresponding symptoms and
                emotional reactions.
              </div>
            </div>
          </div>
        </div>
      </CaseStudySection>

      {/* SECTION 6: IDEATION */}
      <CaseStudySection id="ideation" title="Ideation & Planning" subtitle="From big ideas to tough decision-making." activities={['Blue-sky workshop', 'Affinity mapping', 'Storyboarding', 'Concept evaluation']} background="gray" data-id="element-356">
        <div className="space-y-12" data-id="element-357">
          <p className="max-w-3xl text-lg text-slate-600" data-id="element-358">
            I facilitated remote ideation workshops with the Embr team and
            subject matter experts. Using the "Jobs to be Done" framework, I
            guided the team to generate hundreds of ideas, which I then
            synthesized into narrative storyboards. I used these stories to
            validate concepts with users before we wrote a single line of code,
            ensuring we were building the right solution.
          </p>

          <ImageGallery layout="grid" images={[{
          src: 'https://cdn.magicpatterns.com/uploads/kHgbDQ5pvdNLChRVDP3FfT/embr-storyboards.png',
          alt: 'Remote Ideation Workshop',
          caption: 'Narrative storyboards used to explore and prioritize concepts with users.'
        }, {
          src: 'https://cdn.magicpatterns.com/uploads/rh7E6kNjfb9cMZ3hcvCZhU/embr-planning.png',
          alt: 'Prioritization Mapping',
          caption: 'Prioritization mapping exercises were grounded in user needs and preferences.'
        }]} data-id="element-359" />
        </div>
      </CaseStudySection>

      {/* SECTION 7: THE SOLUTION - 2x2 GRID */}
      <CaseStudySection id="solution" title="The Solution" subtitle="Iterating toward an holistic ecosystem for relief." activities={['Design jams', 'Interactive prototyping', 'Visual design + illustration', 'Diary studies', 'Concept evaluation', 'Usability testing']} background="white" data-id="element-360">
        <div className="space-y-12" data-id="element-361">
          <p className="max-w-3xl text-lg text-slate-600" data-id="element-362">
            Over the course of 9 months, working in Agile sprints with PM and
            Engineering, I designed and tested a range of high-impact features.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-363">
            {features.map((feature, index) => <motion.button key={feature.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} onClick={() => openFeature(feature)} className="group relative overflow-hidden rounded-2xl bg-slate-50 border border-slate-200 p-6 text-left transition-all hover:shadow-xl hover:scale-[1.02] hover:border-blue-300" data-id="element-364">
                {/* Thumbnail */}
                <div className="relative mb-4 overflow-hidden rounded-xl bg-white shadow-md" data-id="element-365">
                  <img src={feature.image} alt={feature.title} className="w-full h-48 object-cover object-center transition-transform duration-500 group-hover:scale-105" data-id="element-366" />
                </div>

                {/* Title & Description */}
                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors" data-id="element-367">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm" data-id="element-368">
                  {feature.description}
                </p>

                {/* Persistent Details Link */}
                <div className="mt-4 flex items-center gap-1 text-sm font-medium text-blue-600" data-id="element-369">
                  <span data-id="element-370">Details</span>
                  <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-371">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-372" />
                  </svg>
                </div>

                {/* Animated bottom border */}
                <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-blue-500 group-hover:w-full transition-all duration-500 ease-out" data-id="element-373" />
              </motion.button>)}
          </div>
        </div>
      </CaseStudySection>

      {/* Feature Modal */}
      <FeatureModal feature={selectedFeature} isOpen={isModalOpen} onClose={closeModal} onPrevious={goToPrevious} onNext={goToNext} hasPrevious={currentIndex > 0} hasNext={currentIndex < features.length - 1} data-id="element-374" />

      {/* SECTION 8: RESULTS & IMPACT */}
      <CaseStudySection id="results" title="Results & Impact" subtitle="Measurable outcomes that transformed the business." background="gray" data-id="element-375">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-376">
          <MetricCard icon={Thermometer} gradient="blue" value="168" suffix="%" label="Increase in Efficacy" description="Clinical study with Johnson & Johnson showed massive improvement over Wave 1" data-id="element-377" />
          <MetricCard icon={Star} gradient="blue" value="4.8" suffix="/5" label="App Store Rating" description="Consistent high ratings from over 3,500 verified users" data-id="element-378" />
          <MetricCard icon={Users} gradient="blue" value="200,000" label="Active Users" description="Relief delivered to thousands more women" data-id="element-379" />
        </div>
      </CaseStudySection>

      <Footer data-id="element-380" />
    </div>;
}